<script setup lang="ts">
/**
 * MY — Declaration of Non-Beneficial Owner (Not a BO, M32110) 문서-뷰 패널.
 * PresetMVP DeclarationNonBoDoc 이식 — Member 선언 레터 + person/address lookup + signers.
 */
import { ref, reactive, computed, provide, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import {
  labelize, typeOf, DECLARATION_NON_BO_REQUIRED,
} from './declarationNonBo/declarationNonBoFields';
import { DECLARATION_NON_BO_DOC_CTX } from './declarationNonBo/declarationNonBoContext';
import {
  buildDeclarationNonBoVariables,
  emptyDeclarationNonBoState,
  type DeclarationNonBoState,
} from '../mappers/declarationNonBoMapper';
import { parseMalaysianAddress, isEmptyMyAddress, formatMyAddressLine, composeMyAddressLine } from '@/shared/utils/malaysianAddress';
import { fetchEntityAddresses } from '@/shared/composables/useEntityAddresses';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import AddressInputModal from './cdd/AddressInputModal.vue';
import DeclarationNonBoDoc from './declarationNonBo/DeclarationNonBoDoc.vue';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  appliedAddress?: { key: string; line: string; town: string; state: string; postcode: string; country: string; seq: number } | null;
  pendingPick?: { value: string; seq: number } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const entityCd = computed(() => String(props.entityData?.entityCd || props.entityData?.icpCd || '').trim());

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());

const state = reactive<DeclarationNonBoState>(emptyDeclarationNonBoState());
const SIGNER_KEY = 'declaration';

const activeKey = ref('');
const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);

function syncMemberToSigners(force = true) {
  const name = String(state.values.member_name || '').trim();
  if (!name) {
    if (force) state.signers[SIGNER_KEY] = [];
    return;
  }
  const cur = String(state.signers[SIGNER_KEY]?.[0]?.name || '').trim();
  if (!force && cur && cur === name) return;
  state.signers[SIGNER_KEY] = [{
    id: `tgt-declaration-${name}`,
    name,
    title: 'Member',
    role: 'recipient',
    showTitle: true,
    _signerLayout: 'box',
  }];
}

function setFieldValue(key: string, val: string) {
  // member_address 등 — BO Notices / BO Letters 와 동일하게 문서용 대문자
  state.values[key] = typeOf(key) === 'address' ? formatMyAddressLine(val) : val;
  if (key === 'member_name') syncMemberToSigners(true);
}

function activate(key: string) {
  activeKey.value = key;
  const t = typeOf(key);
  if (t === 'person' || t === 'entity') {
    emit('request-person-lookup', {
      fieldGroup: 'declaration-non-bo',
      key,
      defaultRole: key === 'member_name' ? 'SHAREHOLDER' : null,
      lookupType: key === 'company_name' || t === 'entity' ? 'entity' : 'officer',
    });
    inputKey.value = '';
  } else {
    inputKey.value = key;
    inputText.value = state.values[key] || '';
    if (t === 'date') dateMenu.value = true;
  }
}
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false;
  commitInput();
}
function commitInput() {
  if (inputKey.value) setFieldValue(inputKey.value, inputText.value.trim());
  inputKey.value = ''; activeKey.value = '';
}
function skipInput() { inputKey.value = ''; activeKey.value = ''; }

function flatAddr(v: any): string {
  return formatMyAddressLine(String(v ?? ''));
}

const pendingPickValue = ref('');
watch(() => props.pendingPick, (p) => { pendingPickValue.value = p?.value || ''; });
function placePick(key: string): boolean {
  if (!pendingPickValue.value) return false;
  setFieldValue(key, pendingPickValue.value);
  pendingPickValue.value = '';
  return true;
}

const addrModal = ref(false);
const addrKey = ref('');
const addrLabel = ref('');
const addrInitial = ref<{ line?: string }>({});
function openAddress(key: string) {
  if (props.studio) { addrKey.value = key; emit('request-address-input', key); return; }
  addrKey.value = key;
  addrLabel.value = labelize(key);
  addrInitial.value = { line: state.values[key] || '' };
  addrModal.value = true;
}
function applyAddressTo(key: string, a: { line: string; town: string; state: string; postcode: string; country: string }) {
  if (!key) return;
  state.values[key] = composeMyAddressLine(a);
}
function applyAddress(a: { line: string; town: string; state: string; postcode: string; country: string }) {
  applyAddressTo(addrKey.value, a);
}
watch(() => props.appliedAddress, (a) => { if (a?.key) applyAddressTo(a.key, a); });

function signersOf(varKey: string): SignerEntry[] { return state.signers[varKey] || []; }
function setSigners(varKey: string, list: SignerEntry[]) {
  state.signers[varKey] = Array.isArray(list) ? list : [];
}

function applyPersonToField(key: string, a: any) {
  const name = a.name || a.contactName || [a.firstName, a.middleName, a.surname].filter(Boolean).join(' ') || '';
  state.values[key] = name;

  const addrs = Array.isArray(a.addresses) ? a.addresses : [];
  const addrFromArr = (x: any) => flatAddr(x?.composed || x?.value || x?.address || '');
  const addrKind = (x: any) => [
    x?.type, x?.addressType, x?.addressTypeName, x?.label, x?.originalLabel,
  ].map((s) => String(s || '')).join(' ');
  const isResidential = (x: any) => /residential|registered|home/i.test(addrKind(x));

  const fullAddr = a.residentialAddress || a.primaryAddress || '';
  const res = a.myResidential && !isEmptyMyAddress(a.myResidential) ? a.myResidential : parseMalaysianAddress(fullAddr);
  const fromParsed = !isEmptyMyAddress(res)
    ? composeMyAddressLine(res)
    : '';
  const addrLine = fromParsed
    || flatAddr(fullAddr)
    || addrFromArr(addrs.find(isResidential))
    || addrFromArr(addrs[0])
    || flatAddr(a.address || '');

  if (addrLine && key === 'member_name') state.values.member_address = addrLine;
  if (key === 'member_name') syncMemberToSigners(true);
}

watch(() => props.selectedPerson, (p) => {
  const ctx = props.activePersonContext;
  if (!p || !ctx || ctx.fieldGroup !== 'declaration-non-bo' || !ctx.key) return;
  applyPersonToField(ctx.key, p as any);
});

provide(DECLARATION_NON_BO_DOC_CTX, {
  valueOf: (k) => state.values[k] || '',
  labelOf: labelize,
  typeOf,
  activeKey,
  activate,
  setFieldValue,
  openAddress,
  entityCd,
  signersOf,
  setSigners,
  pendingPickValue,
  placePick,
});

const entityPrefilled = ref('');
watch(
  () => [entityCd.value, props.entityData] as const,
  async ([cd]) => {
    if (!cd || entityPrefilled.value === cd) return;
    entityPrefilled.value = cd;
    const put = (k: string, v: any, force = false) => {
      const s = String(v || '').trim();
      if (!s) return;
      if (force || !String(state.values[k] || '').trim()) state.values[k] = s;
    };
    put('company_name', props.entityData?.companyName || props.entityData?.entityName, true);
    put('company_registration_no', props.entityData?.registrationNo, true);
    try {
      const addrs = await fetchEntityAddresses(cd);
      const gov = (addrs as any)?.government || addrs;
      if (gov) {
        put('company_name', gov.companyName, false);
        put('company_registration_no', gov.registrationNo || gov.regNo, false);
      }
    } catch { /* noop */ }
  },
  { immediate: true },
);

const overallProgress = computed(() => {
  const list = DECLARATION_NON_BO_REQUIRED;
  if (!list.length) return 0;
  const filled = list.filter((k) => String(state.values[k] || '').trim()).length;
  return Math.round((filled / list.length) * 100);
});
watch(overallProgress, (v) => emit('progress-change', v), { immediate: true });

const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });
onMounted(async () => {
  const code = props.selection?.task?.TASK_CODE;
  if (code) {
    try {
      const cfg = await getTaskConfigByCode(code);
      taskConfigSteps.value = (cfg as any)?.processSteps || [];
    } catch { /* noop */ }
  }
});

const availableTemplates = computed(() => (props.selection?.documents || []).filter((d: any) => d.tmpId));

function tmplName(d: any): string {
  return String(d.templateName || d.docNm || d.name || d.fileName || d.orgFileName || d.docCode || '');
}
function templateFileLabel(d: any, fallback: string): string {
  const raw = tmplName(d).trim() || fallback;
  return raw.replace(/\.docx$/i, '').trim() || fallback;
}
function resolveTemplate(): { tmpId: number; label: string } | null {
  const docs = availableTemplates.value as any[];
  if (!docs.length) return null;
  const re = /non[- ]?bo|not\s*a\s*bo|declaration.*beneficial|beneficial.*status/i;
  const byName = docs.find((d) => re.test(tmplName(d)));
  const hit = byName || docs[0];
  if (!hit?.tmpId) return null;
  return {
    tmpId: hit.tmpId,
    label: templateFileLabel(hit, 'Declaration of Non-Beneficial Owner (Not a BO)'),
  };
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }

  const hit = resolveTemplate();
  if (!hit) {
    progress.value = '⚠ 연결된 문서 템플릿이 없습니다. 관리자에서 Declaration of Non-BO 문서 템플릿을 연결해 주세요.';
    return;
  }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = {
      entityCd: entityCd.value || null,
      presetForm: gatherState(),
      companyName: state.values.company_name || '',
      companyRegistrationNo: state.values.company_registration_no || '',
      letterDate: state.values.letter_date || '',
      memberName: state.values.member_name || '',
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd: props.selection.task.TASK_CODE,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value, taskFlow, officerData,
        }),
      }).then((r) => r.json());
      taskId = taskRes?.data?.taskId || taskRes?.taskId;
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false; return;
      }
    }

    progress.value = 'Preparing document…';
    const formData = buildDeclarationNonBoVariables(state);
    const docSigners = state.signers[SIGNER_KEY] || [];
    try {
      const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          taskId,
          documents: [{
            templateId: hit.tmpId,
            label: hit.label,
            personName: String(state.values.member_name || state.values.company_name || 'Member'),
            entityCd: entityCd.value || undefined,
            formData,
            signers: docSigners.length ? docSigners : undefined,
          }],
          replaceExisting: editMode.value,
        }),
      }).then((r) => r.json());
      if (!saveRes?.success) progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
    } catch (err: any) {
      console.warn('[Declaration Non-BO] document save failed:', err);
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }
    try {
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(`presetTaskDetailPrefetch:${taskId}`, JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }));
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[Declaration Non-BO] create task 실패', e);
    progress.value = `Error: ${e?.message || e}`;
  } finally {
    creating.value = false;
  }
}

function gatherState() {
  return {
    state: JSON.parse(JSON.stringify(state)),
    companyName: state.values.company_name || '',
    companyRegistrationNo: state.values.company_registration_no || '',
    letterDate: state.values.letter_date || '',
    memberName: state.values.member_name || '',
  };
}
function restoreState(s: any) {
  const src = s?.state || s?.presetForm?.state;
  if (src && typeof src === 'object') {
    const base = emptyDeclarationNonBoState();
    Object.assign(state, { ...base, ...src });
    state.values = { ...(src.values || {}) };
    state.signers = { ...(src.signers || {}) };
    // 기존 저장분(소문자 주소)도 문서 표기 규칙에 맞게 대문자로 정규화
    for (const k of Object.keys(state.values)) {
      if (typeOf(k) === 'address' && state.values[k]) {
        state.values[k] = formatMyAddressLine(state.values[k]);
      }
    }
  }
}
defineExpose({ gatherState, restoreState, createTask: handleCreateTask });
</script>

<template>
  <div class="otc-panel" :class="{ 'otc-studio': studio }">
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Declaration of Non-Beneficial Owner (Not a BO)</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')"><v-icon start size="16">mdi-arrow-left</v-icon>Back</v-btn>
    </div>

    <div v-if="!studio" class="co-title-row">
      <span class="ct-label">Task Title</span>
      <v-text-field v-model="titleModel" placeholder="e.g. Declaration of Non-BO — Tilkoblet" variant="outlined" density="compact" hide-details class="ct-input" />
    </div>

    <div v-if="!studio" class="co-entity">
      <v-icon size="18" color="primary">mdi-domain</v-icon>
      <span>{{ entityData?.companyName || entityData?.entityName || 'Entity' }}</span>
      <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">Change</v-btn>
    </div>

    <div v-if="inputKey" class="co-inputbar">
      <div class="lbl">{{ labelize(inputKey) }} <span v-if="typeOf(inputKey) === 'date'" class="opt">(optional)</span></div>
      <div class="row">
        <v-menu v-if="typeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
          <template #activator="{ props: mp }">
            <v-text-field v-bind="mp" v-model="inputText" placeholder="DD/MM/YYYY" variant="outlined" density="compact" hide-details append-inner-icon="mdi-calendar" />
          </template>
          <v-date-picker @update:model-value="onPickDate" />
        </v-menu>
        <v-text-field v-else v-model="inputText" :placeholder="labelize(inputKey)" variant="outlined" density="compact" hide-details autofocus @keydown.enter="commitInput" />
        <v-btn color="primary" size="small" variant="flat" @click="commitInput">Set</v-btn>
        <v-btn v-if="typeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">Skip</v-btn>
      </div>
    </div>

    <div class="co-doc-body">
      <div class="dnb-pkg">
        <div class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">📄</span>
            <span class="nm">Declaration of Non-Beneficial Owner (Not a BO)</span>
            <span class="no">Doc 1 / 1</span>
          </div>
          <div class="pkg-paper">
            <DeclarationNonBoDoc />
          </div>
        </div>
      </div>
    </div>

    <div v-if="!studio || progress" class="co-footer">
      <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">Back</v-btn>
      <div class="co-foot-right">
        <span v-if="progress" class="co-progress" :class="{ err: progress.startsWith('Error') || progress.includes('⚠') }">{{ progress }}</span>
        <v-btn
          v-if="!studio"
          color="primary"
          class="text-none"
          :loading="creating"
          :disabled="creating"
          :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
          @click="handleCreateTask"
        >{{ editMode ? 'Update Task' : 'Create Task' }}</v-btn>
      </div>
    </div>

    <AddressInputModal v-model="addrModal" :label="addrLabel" :has-components="false" :initial="addrInitial" @apply="applyAddress" />
  </div>
</template>

<style scoped>
.otc-panel { display:flex; flex-direction:column; }
.otc-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.otc-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; } .co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.co-title-row .ct-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-title-row .ct-input { max-width:520px; }
.co-entity { display:flex; align-items:center; gap:8px; background:#f0f5ff; padding:8px 14px; font-size:13px; }
.co-inputbar { padding:10px 14px; background:#fafbfe; border-bottom:1px solid #eef0f6; position:sticky; top:0; z-index:5; }
.co-inputbar .lbl { font-size:12px; font-weight:700; color:#1B84FF; margin-bottom:6px; } .co-inputbar .opt { color:#8a94a6; font-weight:400; }
.co-inputbar .row { display:flex; gap:8px; align-items:center; }
.co-doc-body { padding:16px 12px 28px; }
.co-footer { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-top:1px solid #eef0f6; gap:12px; }
.co-foot-right { display:flex; align-items:center; gap:12px; margin-left:auto; }
.co-progress { font-size:12px; color:#5b6472; } .co-progress.err { color:#c0392b; }
.dnb-pkg { display:flex; flex-direction:column; gap:10px; width:100%; max-width:900px; margin:0 auto; }
.pkg-doc { width: 100%; margin: 0 auto; }
.pkg-doclabel { display: flex; align-items: center; gap: 7px; padding: 0 4px 9px; }
.pkg-doclabel .ic { font-size: 14px; line-height: 1; }
.pkg-doclabel .nm { font-size: 12px; font-weight: 800; color: #42506a; letter-spacing: .01em; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.pkg-paper {
  background: #fff; padding: 34px 38px;
  box-shadow: 0 4px 24px rgba(20, 30, 60, .12);
  font-family: Arial, 'Malgun Gothic', sans-serif;
  color: #111; font-size: 12px; line-height: 1.4; border-radius: 2px;
}
</style>
